import useInstantPayEligible from '@/enterprise/components/InstantPay/useInstantPayEligible';
import { CombinedPricingSuggestion } from '@/enterprise/data/usePricingSuggestion';
import { OfferOptionType, TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { OfferBuilderState } from './OfferBuilderContext';
import { buildTakerMarketKey, IsInstantPayEligible } from './utils';

export type PreferredStepSkipReason =
  | 'not-one-time'
  | 'not-apr'
  | 'no-markets-selected'
  | 'not-preferred-eligible'
  | 'rate-not-above-preferred'
  | 'instant-pay-eligible'
  | 'pricing-disabled';

export const isPreferredEligibleMarket = (takerMarket: TakerMarket): boolean => {
  if (takerMarket.marketPricingType !== 'PRICE_DISCOVERY') {
    return false;
  }

  const hasPreferredInNewOffer = takerMarket.offerOptions?.newOffer?.some(
    (offerOption) => offerOption.type === OfferOptionType.PREFERRED_TERM
  );

  const hasPreferredInAvailableOffer = takerMarket.offerOptions?.availableOffer?.some(
    (offerOption) => offerOption.type === OfferOptionType.PREFERRED_TERM
  );

  return !!(hasPreferredInNewOffer || hasPreferredInAvailableOffer);
};

export const shouldIncludePreferred = (
  state: OfferBuilderState,
  isInstantPayEligible?: IsInstantPayEligible
): boolean => {
  if (state.selected.length === 0) {
    return false;
  }

  if (!state.selected.every(isPreferredEligibleMarket)) {
    return false;
  }

  // Preferred terms would replace the Instant Pay offer
  if (isInstantPayEligible && state.selected.some(isInstantPayEligible)) {
    return false;
  }

  // Every selected market must have no active offer and no offer schedule
  if (state.selected.some((takerMarket) => takerMarket.offerConfig.isEnabled)) {
    return false;
  }

  if (state.selected.some((takerMarket) => takerMarket.offerSchedule)) {
    return false;
  }

  return true;
};

export const shouldShowPreferredStep = (
  state: OfferBuilderState,
  pricingData?: CombinedPricingSuggestion | null,
  isInstantPayEligible?: IsInstantPayEligible
): boolean => {
  if (state.frequency !== 'oneTime') {
    return false;
  }

  if (state.rateType !== 'APR') {
    return false;
  }

  if (!shouldIncludePreferred(state, isInstantPayEligible)) {
    return false;
  }

  // Only show the preferred step when the user's selected rate is higher than the preferred rate
  // and all selected markets are projected to clear 100% of invoices.
  // If pricing data is not yet available, allow the step to show (the guard in PreferredRateStep handles the redirect).
  if (pricingData) {
    return state.selected.every((takerMarket) => {
      const supplierMarket = pricingData.supplierMarkets.find(
        (sm) => sm.marketUuid === takerMarket.marketUuid && sm.supplierDivisionUuid === takerMarket.takerDivisionUuid
      );
      const preferredRate = supplierMarket?.preferred?.oneTime?.rate;
      const selectedRate = state.workingTakerMarkets[buildTakerMarketKey(takerMarket)]?.rate;

      if (preferredRate === undefined || selectedRate === undefined) {
        return false;
      }

      return selectedRate > preferredRate;
    });
  }

  return true;
};

/**
 * Returns why the preferred rate upgrade step was skipped for the given state and pricing data.
 * Returns null when the step would not be skipped (i.e. it should show).
 */
export const getPreferredStepSkipReason = (
  state: OfferBuilderState,
  pricingData?: CombinedPricingSuggestion | null,
  isInstantPayEligible?: IsInstantPayEligible
): PreferredStepSkipReason | null => {
  if (state.frequency !== 'oneTime') {
    return 'not-one-time';
  }

  if (state.rateType !== 'APR') {
    return 'not-apr';
  }

  if (state.selected.length === 0) {
    return 'no-markets-selected';
  }

  if (!state.selected.every(isPreferredEligibleMarket)) {
    return 'not-preferred-eligible';
  }

  if (isInstantPayEligible && state.selected.some(isInstantPayEligible)) {
    return 'instant-pay-eligible';
  }

  if (pricingData) {
    const allAbovePreferred = state.selected.every((takerMarket) => {
      const supplierMarket = pricingData.supplierMarkets.find(
        (sm) => sm.marketUuid === takerMarket.marketUuid && sm.supplierDivisionUuid === takerMarket.takerDivisionUuid
      );
      const preferredRate = supplierMarket?.preferred?.oneTime?.rate;
      const selectedRate = state.workingTakerMarkets[buildTakerMarketKey(takerMarket)]?.rate;

      if (preferredRate === undefined || selectedRate === undefined) {
        return false;
      }

      return selectedRate > preferredRate;
    });

    if (!allAbovePreferred) {
      return 'rate-not-above-preferred';
    }
  }

  return null;
};

interface PreferredRateEligibility {
  showPreferredStep: boolean;
  includePreferred: boolean;
  skipReason: PreferredStepSkipReason | null;
}

const usePreferredRateEligibility = (
  state: OfferBuilderState,
  pricingData?: CombinedPricingSuggestion | null
): PreferredRateEligibility => {
  const { isInstantPayEligible } = useInstantPayEligible();

  return {
    showPreferredStep: shouldShowPreferredStep(state, pricingData, isInstantPayEligible),
    includePreferred: shouldIncludePreferred(state, isInstantPayEligible),
    skipReason: getPreferredStepSkipReason(state, pricingData, isInstantPayEligible),
  };
};

export default usePreferredRateEligibility;
